import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { ApiProvider, useApi } from './api/ApiContext';
import type { Dependencies, ResolvedDependencies } from './app/dependencies';
import { createDependencies } from './app/dependencies';
import { AuthProvider } from './auth/AuthContext';
import { runtimeConfig } from './config/runtimeConfig';
import './index.css';
import { createCateringApiService } from './services/cateringApiService';

const rootElement = document.getElementById('root');
const root = rootElement ? createRoot(rootElement) : undefined;

function requireCateringService(dependencies: Dependencies): ResolvedDependencies {
  const { cateringService } = dependencies;
  if (!cateringService) {
    throw new Error('Mock dependencies must provide a catering service.');
  }

  return {
    ...dependencies,
    cateringService,
  };
}

function AuthenticatedApp({ dependencies }: { dependencies: Dependencies }) {
  const api = useApi();
  const cateringService = useMemo(() => createCateringApiService(api), [api]);

  return (
    <App
      dependencies={{
        ...dependencies,
        cateringService,
      }}
    />
  );
}

function RuntimeApp({ dependencies }: { dependencies: Dependencies }) {
  if (runtimeConfig.useMocks) {
    return <App dependencies={requireCateringService(dependencies)} />;
  }

  return (
    <AuthProvider>
      <ApiProvider>
        <AuthenticatedApp dependencies={dependencies} />
      </ApiProvider>
    </AuthProvider>
  );
}

function renderApp(dependencies: Dependencies): void {
  root?.render(
    <FluentProvider theme={webLightTheme}>
      <RuntimeApp dependencies={dependencies} />
    </FluentProvider>,
  );
}

async function bootstrap(): Promise<void> {
  const dependencies = await createDependencies();
  renderApp(dependencies);
}

if (typeof Office === 'undefined') {
  void bootstrap();
} else {
  Office.onReady(() => {
    void bootstrap();
  });
}
